import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  CounterType,
  DateTypeFilter,
  formatDateLocalized,
  getFullName,
  RoutineType,
  SortDirectionEnum,
  useCustomQuery,
  useFilteredWorkspaceRoutines,
  WorkflowRoutineConfig,
  WorkspaceRoutine,
  useFindByQuery,
} from '@gripp/shared-logic';
import { Colors } from '@gripp/shared-ui';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';

type Props = {
  label: string;
  routineIds: string[];
  workspaceId: string;
  dateFilter?: {
    dateType: DateTypeFilter;
    startDate?: string;
    endDate?: string;
  };
  hideButton?: boolean;
};

type CSVData = {
  headers: any[];
  rows: any[];
  filename: string;
};

export const DownloadRoutine = forwardRef<
  { triggerDownload: () => void },
  Props
>((props, ref) => {
  const {
    label,
    routineIds,
    workspaceId,
    dateFilter,
    hideButton = false,
  } = props || {};
  const { t } = useTranslation();
  const csvRefs = useRef<(any | null)[]>([]);
  const [csvDataList, setCsvDataList] = useState<CSVData[]>([]);
  const [doExport, toggleExport] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [currentDownloadIndex, setCurrentDownloadIndex] = useState(0);
  const [uniqueVersionIds, setUniqueVersionIds] = useState<string[]>([]);

  // Expose download function via ref
  useImperativeHandle(ref, () => ({
    triggerDownload: onDownload,
  }));

  const routines = useFilteredWorkspaceRoutines(
    { id: { in: routineIds } },
    workspaceId
  );

  const getDateFilter = () => {
    if (!dateFilter || dateFilter.dateType === DateTypeFilter.ALL) {
      return {};
    }

    if (dateFilter.dateType === DateTypeFilter.CUSTOMDATE) {
      const filter: any = {};
      if (dateFilter.startDate) {
        // Convert date string to ISO format
        const startDate = new Date(dateFilter.startDate);
        filter.ge = startDate.toISOString();
      }
      if (dateFilter.endDate) {
        // Convert date string to ISO format and set to end of day
        const endDate = new Date(dateFilter.endDate);
        endDate.setHours(23, 59, 59, 999); // Set to end of day
        filter.le = endDate.toISOString();
      }
      return filter;
    }

    // For predefined date ranges, we need to calculate the date range
    const now = new Date();
    let startDate: Date;
    const endDate: Date = now;

    switch (dateFilter.dateType) {
      case DateTypeFilter.TODAY:
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case DateTypeFilter.THISWEEK:
        const dayOfWeek = now.getDay();
        const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        startDate = new Date(now.getFullYear(), now.getMonth(), diff);
        break;
      case DateTypeFilter.THISMONTH:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      default:
        return {};
    }

    return {
      ge: startDate.toISOString(),
      le: endDate.toISOString(),
    };
  };

  const { data } = useCustomQuery({
    query: GET_ROUTINE_DATA,
    cacheKey: ['exportRoutineData', routineIds, dateFilter],
    variables: {
      filter: {
        routine: { in: routineIds },
        ...(Object.keys(getDateFilter()).length > 0 && {
          createdAt: getDateFilter(),
        }),
      },
      orderBy: { field: 'createdAt', order: SortDirectionEnum.Asc },
    },
    options: {
      enabled: doExport,
      gcTime: 10000,
      staleTime: 10000,
    },
  });

  // Extract unique version IDs from the data
  const [versionIdsExtracted, setVersionIdsExtracted] = useState(false);
  
  useEffect(() => {
    if (!data) {
      setVersionIdsExtracted(false);
      return;
    }

    const versionIds = new Set<string>();
    data.findAssetRoutines.items.forEach((item: any) => {
      if (item.routineVersion?.id) {
        versionIds.add(item.routineVersion.id);
      }
    });

    const uniqueIds = Array.from(versionIds);
    setUniqueVersionIds(uniqueIds);
    setVersionIdsExtracted(true);
  }, [data]);

  const { data: versionData, isLoading: isVersionsLoading } = useFindByQuery({
    query: GET_ROUTINE_VERSIONS,
    modelName: 'workspaceRoutineVersion',
    variables: {
      filter: {
        id: { in: uniqueVersionIds },
      },
    },
    options: {
      enabled: uniqueVersionIds.length > 0,
    },
  });

  const versionConfigsMap = useMemo(() => {
    const map = new Map<string, any>();
    if (versionData?.items) {
      versionData.items.forEach((version: any) => {
        map.set(version.id, version);
      });
    }
    return map;
  }, [versionData]);

  const baseHeaders = useMemo(
    () => [
      { label: 'Asset', key: 'asset' },
      { label: 'Created By', key: 'createdBy' },
      { label: 'Created Date', key: 'createdDate' },
      { label: 'Created Time', key: 'createdTime' },
      { label: 'Groups', key: 'groups' },
    ],
    []
  );

  const onDownload = () => {
    if (csvDataList?.length > 0) {
      // user already clicked on icon 1x, so just download it
      if (csvDataList.length === 1) {
        // Single file download
        setTimeout(() => {
          csvRefs.current[0]?.link?.click();
        }, 50);
      } else {
        // Multiple files download - download them sequentially
        setCurrentDownloadIndex(0);
        downloadNextFile();
      }
    } else {
      toggleExport(true);
      // setLabel('exporting');
    }
  };

  const downloadNextFile = useCallback(() => {
    if (currentDownloadIndex >= csvDataList.length) {
      // All files downloaded
      toggleExport(false);
      setCurrentDownloadIndex(0);
      return;
    }

    setTimeout(() => {
      csvRefs.current[currentDownloadIndex]?.link?.click();
      setCurrentDownloadIndex((prev) => prev + 1);
    }, 100);
  }, [currentDownloadIndex, csvDataList.length]);

  // Trigger next download when currentDownloadIndex changes
  useEffect(() => {
    if (
      doExport &&
      csvDataList.length > 1 &&
      currentDownloadIndex > 0 &&
      currentDownloadIndex < csvDataList.length
    ) {
      downloadNextFile();
    }
  }, [currentDownloadIndex, doExport, csvDataList.length, downloadNextFile]);

  const getBaseRowData = useCallback((item: any) => {
    return {
      asset: item.asset.name,
      createdBy: getFullName(item.createdBy.name),
      createdDate: item.createdAt
        ? new Date(item.createdAt).toISOString().split('T')[0]
        : '', // YYYY-MM-DD
      createdTime: item.createdAt
        ? new Date(item.createdAt).toISOString().split('T')[1].substring(0, 8)
        : '', // HH:MM:SS
      groups:
        item.workspaceAsset.groups?.length > 0
          ? item.workspaceAsset.groups
              .filter((g: any) => g && g.name)
              .map((g: any) => g.name)
              .join(', ')
          : '',
    };
  }, []);

  const getResponseKey = useCallback((response: any) => {
    const keys = Object.keys(response);
    const valueKeys = [
      'value',
      'confirmedValue',
      'count',
      'status',
      'option',
      'options',
      'mediaIds',
      'timeTypeValue',
    ];
    return keys.find((key) => valueKeys.includes(key));
  }, []);

  const createWorkflowData = useCallback(
    (items: any[], workspaceRoutine: WorkspaceRoutine): CSVData => {
      // Collect all unique questions from all versions in the data
      const allQuestionsMap = new Map<string, { id: string; instructions: string; order: number }>();
      
      items.forEach(item => {
        const versionId = item.routineVersion?.id;
        const versionData = versionId ? versionConfigsMap.get(versionId) : null;
        
        if (versionData?.config?.pages) {
          const versionConfig = versionData.config as WorkflowRoutineConfig;
          versionConfig.pages.forEach(page => {
            page.questions?.forEach(question => {
              if (!allQuestionsMap.has(question.id)) {
                allQuestionsMap.set(question.id, {
                  id: question.id,
                  instructions: question.instructions || '',
                  order: question.order
                });
              }
            });
          });
        }
      });

      // Sort questions by order to maintain some consistency
      const allQuestions = Array.from(allQuestionsMap.values())
        .sort((a, b) => a.order - b.order);

      const headers = [
        ...baseHeaders,
        ...allQuestions.map((question) => ({
          label: question.instructions,
          key: question.id,
        })),
      ];

      const rows = items.map((item) => {
        const baseRowData = getBaseRowData(item);
        const row: any = {
          ...baseRowData,
        };
        
        // Map responses to question columns
        item.data.responses.forEach((response: any) => {
          const valueKey = getResponseKey(response);
          row[response.questionId] = formatResponseValue(response, valueKey);
        });
        
        return row;
      });

      return {
        headers,
        rows,
        filename: `${workspaceRoutine.config.title}-routine-data.csv`,
      };
    },
    [baseHeaders, getBaseRowData, getResponseKey, versionConfigsMap]
  );

  const createNonWorkflowData = useCallback(
    (items: any[], workspaceRoutine: WorkspaceRoutine): CSVData => {
      const config = workspaceRoutine.config as any;
      const isCumulativeOrTally =
        workspaceRoutine.type === RoutineType.Counter &&
        (config?.counterType === CounterType.Cumulative ||
         config?.counterType === CounterType.Tally);

      const headers = [
        ...baseHeaders,
        { label: workspaceRoutine.config.title, key: 'data' },
      ];

      if (isCumulativeOrTally) {
        headers.push({
          label: `${workspaceRoutine.config.title} - Delta`,
          key: 'delta',
        });
      }

      const rows = items.map((item) => {
        const baseRowData = getBaseRowData(item);
        const row: any = {
          ...baseRowData,
          data: Object.values(item.data)[0], // there is only 1 "value" on non-workflow routines, so this will get "count", "confirmedValue", "value", etc.
        };

        if (isCumulativeOrTally) {
          row.delta = item.data.userCount?.toString() ?? '';
        }

        return row;
      });

      return {
        headers,
        rows,
        filename: `${workspaceRoutine.config.title}-routine-data.csv`,
      };
    },
    [baseHeaders, getBaseRowData]
  );

  useEffect(() => {
    if (!data || !versionIdsExtracted) {
      return;
    }
    
    if (uniqueVersionIds.length > 0 && (isVersionsLoading || versionConfigsMap.size === 0)) {
      return;
    }

    const newCsvDataList: CSVData[] = [];

    routines.forEach((routine) => {
      const routineItems = data.findAssetRoutines.items.filter(
        (item: any) => item.routine.id === routine.id
      );

      if (routineItems.length > 0) {
        if (routine.type === RoutineType.Workflow) {
          newCsvDataList.push(createWorkflowData(routineItems, routine));
        } else {
          newCsvDataList.push(createNonWorkflowData(routineItems, routine));
        }
      }
    });

    setCsvDataList(newCsvDataList);
  }, [data, routines, createWorkflowData, createNonWorkflowData, uniqueVersionIds, versionConfigsMap, isVersionsLoading, versionIdsExtracted]);

  // Separate effect for triggering download when doExport changes
  useEffect(() => {
    if (doExport && csvDataList.length > 0) {
      if (csvDataList.length === 1) {
        // Single file download
        setTimeout(() => {
          csvRefs.current[0]?.link?.click();
          toggleExport(false);
          setCurrentDownloadIndex(0);
        }, 50);
      } else {
        // Multiple files download - download them sequentially
        setCurrentDownloadIndex(0);
        downloadNextFile();
      }
    }
  }, [doExport, csvDataList.length]);

  return (
    <>
      {!hideButton && (
        <Pressable
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
          style={({ pressed }) => [
            styles.customButton,
            styles.buttonStyle,
            isHovered && styles.hovered,
            pressed && styles.pressed,
          ]}
          onPress={onDownload}
          disabled={doExport}
        >
          <View style={{ flexDirection: 'row' }}>
            <MaterialCommunityIcons
              name="download"
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            <Text style={styles.label}>{label}</Text>
          </View>
        </Pressable>
      )}

      {/* Render CSV links for each routine */}
      {csvDataList.map((csvData, index) => (
        <CSVLink
          key={index}
          style={{ display: 'none' }}
          ref={(el: any) => (csvRefs.current[index] = el)}
          data={csvData.rows}
          headers={csvData.headers}
          filename={csvData.filename}
        />
      ))}
    </>
  );
});

const styles = StyleSheet.create({
  icon: {
    marginRight: 4,
  },
  label: {
    fontSize: 16,
    fontWeight: 500,
    letterSpacing: -0.25,
  },
  customButton: {
    paddingHorizontal: 6,
    paddingVertical: 5,
    borderRadius: 8,
  },
  hovered: {
    backgroundColor: Colors.primaryGrayHeader,
  },
  pressed: {
    backgroundColor: Colors.blackWithAlpha,
  },
  buttonStyle: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: 128,
    height: 32,
    backgroundColor: Colors.white,
    borderRadius: 16,
  },
});

const GET_ROUTINE_DATA = `
  query FindAssetRoutines($filter: AssetRoutineFilter, $orderBy: OrderByInput) {
    findAssetRoutines(filter: $filter, orderBy: $orderBy) {
      items {
        routine {
          id
        }
        routineVersion {
          id
        }
        asset {
          name
        }
        workspaceAsset {
          groups {
            name
          }
        }
        createdAt
        createdBy {
          name {
            first
            last
          }
        }
        data
      }
    }
  }
`;

const GET_ROUTINE_VERSIONS = `
  query FindWorkspaceRoutineVersions($filter: WorkspaceRoutineVersionFilter) {
    findWorkspaceRoutineVersions(filter: $filter) {
      items {
        id
        type
        config
      }
    }
  }
`;

function formatResponseValue(response: any, valueKey?: string) {
  const value = response[valueKey as string];

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (value && valueKey === 'timeTypeValue') {
    if (
      typeof value === 'object' &&
      'startTime' in value &&
      'endTime' in value
    ) {
      return `${formatDateLocalized(value.startTime)} - ${formatDateLocalized(
        value.endTime
      )}`;
    } else if (typeof value === 'string') {
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        return formatDateLocalized(value);
      }
      return value;
    }
  }

  return value;
}
